import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { tvdData, TvdDrillDownGrouping, TVDMetricDrilldownDetails } from '../../types/tvd-types';
import { CurrencyUomService, UomOption } from 'src/app/core/services/currency-uom.service';
import { eroiService } from '../../services/eroi-metric.service';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { Subscription } from 'rxjs';
import { ParseNumberPipe } from 'src/app/core/pipes/parse-number.pipe';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { Router } from '@angular/router';
import { TVDService } from '../../services/tvd.service';
import { InfoTooltip } from 'src/app/core/types/tooltip-types';

@Component({
    selector: 'composite-eroi-metric',
    templateUrl: './composite-eroi-metric.component.html',
    styleUrls: ['./composite-eroi-metric.component.scss'],
    providers: [ParseNumberPipe],
    standalone: false
})
export class CompositeEroiMetricComponent implements OnInit, OnChanges {
  @Input() id: string = "metric_id"; // TODO: Why is the default "metric_id"?
  @Input() groupId: string | undefined = "";
  @Input() data: tvdData[] = [];
  @Input() makingSelectionsState: boolean = false;
  @Input() showInfoTooltip: boolean = false;
  @Input() tooltipText: string = "";
  @Input() measurementSystem: UomOption = 'Imperial';

  private displayInRoundedNumberSubscription: Subscription | undefined;
  private currencyCodeSubscription: Subscription | undefined;
  private showInEnterpriseTermsSubscription: Subscription | undefined;

  monetaryContribution: string = "0";
  valueContribution: string = "0";
  modifiedMonetaryContribution: string = "0";
  modifiedValueContribution: string = "0";
  displayMonetaryContribution: string = "0";
  displayValueContribution: string = "0";
  currencyCode: string = "USD";
  isLoading: boolean = true;
  displayInRoundedNumber: boolean = false;
  showInEnterpriseTerms: boolean = false;

  infoTooltipType = InfoTooltip; // Expose enum to template

  get isPestLaborProductivityDivisionMode(): boolean {
    return this.id === 'labor_productivity' && !this.showInEnterpriseTerms;
  }

  constructor(
    private _currencyUomService: CurrencyUomService,
    private _eroiService: eroiService,
    private _uiPreferencesService: UIPreferencesService,
    private _parseNumberPipe: ParseNumberPipe,
    private _router: Router,
    private _mixpanelService: MixpanelService,
    private _tvdService: TVDService
  ) {}

  ngOnInit(): void {
    this.displayInRoundedNumberSubscription = this._uiPreferencesService.displayRoundedValuesObservable.subscribe(f => this.displayInRoundedNumber = f);
    this.showInEnterpriseTermsSubscription = this._tvdService.showInEnterpriseTermsObservable.subscribe(f => this.showInEnterpriseTerms = f);
    this.currencyCodeSubscription = this._currencyUomService.displayedCurrency$.subscribe(() => {
      this.currencyCode = this._currencyUomService.getDisplayedCurrency();
    });
    this.calculateCompositeValues();
  }

  ngOnChanges(): void {
    this.calculateCompositeValues();
  }

  ngOnDestroy(): void {
    this.displayInRoundedNumberSubscription?.unsubscribe();
    this.currencyCodeSubscription?.unsubscribe();
    this.showInEnterpriseTermsSubscription?.unsubscribe();
  }

  calculateCompositeValues() {
    if(this.data.length > 0)
    {
      this.monetaryContribution = this.data.reduce((acc, curr) => (Number(curr.monetaryContribution?.replace(/,/g, '') ?? '') || 0) + acc, 0).toLocaleString();
      this.valueContribution = this.data.reduce((acc, curr) => (Number(curr.valueContribution?.replace(/,/g, '') ?? '') || 0) + acc, 0).toLocaleString();

      // Setting modified contributions for consistency as they are for automated TVD metrics
      this.modifiedMonetaryContribution = this.data.reduce((acc, curr) => (Number(curr.modifiedMonetaryContribution?.replace(/,/g, '') ?? '') || 0) + acc, 0).toLocaleString();
      this.modifiedValueContribution = this.data.reduce((acc, curr) => (Number(curr.modifiedValueContribution?.replace(/,/g, '') ?? '') || 0) + acc, 0).toLocaleString();

      this.displayMonetaryContribution = this.data.reduce((acc, curr) => {
        const modifiedValue = Number(curr.modifiedMonetaryContribution?.replace(/,/g, '') ?? '') || 0;
        const monetaryValue = Number(curr.monetaryContribution?.replace(/,/g, '') ?? '') || 0;

        return acc + (modifiedValue > 0 ? modifiedValue : monetaryValue);
      }, 0).toLocaleString();

      this.displayValueContribution = this.data.reduce((acc, curr) => {
        const modifiedValue = Number(curr.modifiedValueContribution?.replace(/,/g, '') ?? '') || 0;
        const value = Number(curr.valueContribution?.replace(/,/g, '') ?? '') || 0;

        return acc + (modifiedValue > 0 ? modifiedValue : value);
      }, 0).toLocaleString();
      
    }
  }

   ViewDetailsByLocation() {
    //navigate to locations/products details page by group type
    this._router.navigate(['/total-value-delivery-details'],
    {
      queryParams: {
        metricId: this.id,
        groupId: this.groupId,
        groupBy: TvdDrillDownGrouping.Location,
        metricName: this._eroiService.mapIdToTitle(this.id), // TODO: Consider passing the EROI ID instead of title to avoid the need for mapping in the details page.
      }
    });
    this._mixpanelService.track('TVD location Drilldown Details Clicked', {
      MetricName: this._eroiService.mapIdToTitle(this.id), // TODO: Consider passing the EROI ID instead of title to avoid the need for mapping in the details page.
      TVDDollars: this._parseNumberPipe.transform(this.monetaryContribution),
      TVDMetric: this._parseNumberPipe.transform(this.modifiedValueContribution),
    });
  }
}
